import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { rgba } from 'polished';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';
import moment from 'moment';

import { ChevronRightIcon } from '@treams/treams-icons';
import { Skeleton } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import GoalCard from './GoalCard';

const Container = styled(Flex)`
	flex-direction: column;
	background: ${(props) => rgba(props.theme.colors.employee, 0.25)};
	border-radius: 5px;
	color: ${(props) => props.theme.colors.employee};
	min-height: 80px;
	justify-content: center;
`;

const Chevron = styled(Box)`
	width: 24px;
	height: 24px;

	svg {
		display: block;

		path {
			fill: ${(props) => props.theme.colors.employee};
		}
	}
`;

const Link = styled(Box)`
	cursor: pointer;
	text-decoration: underline;

	&:hover {
		text-decoration: none;

		&:active {
			color: black;
		}
	}
`;

export default function RemainingGoalsStatus(props: { momentID: string }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const query = graphql.useMomentForGoalsQuery({ variables: { id: props.momentID } });
	const [expanded, setExpanded] = useState(false);

	if (!query.data) {
		return <Skeleton.Box height={80} mb={4} />;
	}

	const regularRequired = Math.max(
		1,
		query.data.momentInstance.momentTemplate.goals?.minimumGoals ?? 0,
	);
	const competencyRequired =
		query.data.momentInstance.momentTemplate.goals?.minimumCompetencyGoals ?? 0;

	const regularCount = query.data.momentInstance.goals?.length ?? 0;
	const competencyCount =
		query.data.momentInstance.goals?.filter((goal) => !!goal.competency).length ?? 0;

	const competencyRemaining = Math.max(0, competencyRequired - competencyCount);
	const regularRemaining = Math.max(0, regularRequired - regularCount);

	return (
		<Container p={3} mb={4}>
			<Flex>
				<Box flexGrow={1} flexShrink={1}>
					{competencyRemaining === 0
						? t('goals.createFromMoment.remaining.simple', {
								regular: regularRemaining,
						  })
						: t('goals.createFromMoment.remaining.title', {
								competency: competencyRemaining,
								regular: Math.max(0, regularRemaining - competencyRemaining),
						  })}
					<strong> {localize(query.data.momentInstance.momentTemplate.title).text}</strong>
				</Box>
				<Box flexGrow={0} flexShrink={0} ml={2}>
					{t('common.dueDate')}: {moment().format('DD MMM YYYY')}
				</Box>
			</Flex>
			{(query.data.momentInstance.goals?.length ?? 0) > 0 && (
				<Box mt={2}>
					{expanded ? (
						<Box>
							{(query.data.momentInstance.goals ?? []).map((goal) => (
								<Box key={goal.id} mb={2}>
									<GoalCard goal={goal as graphql.Goal} small />
								</Box>
							))}
						</Box>
					) : (
						<Flex alignItems="center">
							<Chevron>
								<ChevronRightIcon />
							</Chevron>
							<Link onClick={() => setExpanded(true)}>
								{t('goals.createFromMoment.remaining.chevron')}
							</Link>
						</Flex>
					)}
				</Box>
			)}
		</Container>
	);
}
